/**
 * Shared party-ledger posting helpers.
 * Client AR: + increases receivable; − reduces (receipt / credit note).
 * Vendor AP: + increases payable; − reduces (payment / debit note).
 */

import { supabase } from "@/lib/supabase";

export type PartyKind = "client" | "vendor";

export type ClientArKind = "opening" | "invoice" | "receipt" | "credit_note" | "adjustment";
export type VendorApKind = "opening" | "bill" | "payment" | "debit_note" | "adjustment";

export type PartyMovementInput = {
  party: PartyKind;
  partyId: string;
  kind: ClientArKind | VendorApKind;
  amount: number;
  movementDate: string;
  reference?: string | null;
  notes?: string | null;
  sourceType?: string | null;
  sourceId?: string | null;
};

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

async function recomputeClientOutstanding(clientId: string) {
  const { data: moves, error: sumError } = await supabase
    .from("client_ar_movements")
    .select("amount")
    .eq("client_id", clientId);
  if (sumError) throw sumError;

  const outstanding = Math.max(0, (moves ?? []).reduce((s, m) => s + money(m.amount), 0));

  const { data: current, error: curError } = await supabase
    .from("clients")
    .select("overdue_balance")
    .eq("id", clientId)
    .single();
  if (curError) throw curError;

  const overdue = Math.min(money(current.overdue_balance), outstanding);
  const { error: updError } = await supabase
    .from("clients")
    .update({ outstanding_balance: outstanding, overdue_balance: overdue })
    .eq("id", clientId);
  if (updError) throw updError;
  return outstanding;
}

async function recomputeVendorOutstanding(vendorId: string) {
  const { data: moves, error: sumError } = await supabase
    .from("vendor_ap_movements")
    .select("amount")
    .eq("vendor_id", vendorId);
  if (sumError) throw sumError;

  const outstanding = Math.max(0, (moves ?? []).reduce((s, m) => s + money(m.amount), 0));
  const { error: updError } = await supabase
    .from("vendors")
    .update({ outstanding_balance: outstanding })
    .eq("id", vendorId);
  if (updError) throw updError;
  return outstanding;
}

/** Insert or replace a movement keyed by source_type + source_id when provided. */
export async function postPartyMovement(input: PartyMovementInput) {
  const payload = {
    movement_date: input.movementDate,
    kind: input.kind,
    amount: input.amount,
    reference: input.reference?.trim() || null,
    notes: input.notes?.trim() || null,
    source_type: input.sourceType ?? null,
    source_id: input.sourceId ?? null,
  };

  if (input.party === "client") {
    if (input.sourceType && input.sourceId) {
      await supabase
        .from("client_ar_movements")
        .delete()
        .eq("source_type", input.sourceType)
        .eq("source_id", input.sourceId);
    }
    const { data, error } = await supabase
      .from("client_ar_movements")
      .insert({ ...payload, client_id: input.partyId })
      .select("*")
      .single();
    if (error) throw error;
    await recomputeClientOutstanding(input.partyId);
    return data;
  }

  if (input.sourceType && input.sourceId) {
    await supabase
      .from("vendor_ap_movements")
      .delete()
      .eq("source_type", input.sourceType)
      .eq("source_id", input.sourceId);
  }
  const { data, error } = await supabase
    .from("vendor_ap_movements")
    .insert({ ...payload, vendor_id: input.partyId })
    .select("*")
    .single();
  if (error) throw error;
  await recomputeVendorOutstanding(input.partyId);
  return data;
}

export async function syncClientOpeningBalance(
  clientId: string,
  openingBalance: number,
  asOf: string | null,
) {
  const { error: delError } = await supabase
    .from("client_ar_movements")
    .delete()
    .eq("client_id", clientId)
    .eq("kind", "opening");
  if (delError) throw delError;

  if (asOf && openingBalance !== 0) {
    const { error: insError } = await supabase.from("client_ar_movements").insert({
      client_id: clientId,
      movement_date: asOf,
      kind: "opening",
      amount: openingBalance,
      reference: "Opening balance",
      notes: "Brought-forward AR for ledger matching",
      source_type: "opening",
      source_id: clientId,
    });
    if (insError) throw insError;
  }

  await recomputeClientOutstanding(clientId);
}

export async function syncVendorOpeningBalance(
  vendorId: string,
  openingBalance: number,
  asOf: string | null,
) {
  const { error: delError } = await supabase
    .from("vendor_ap_movements")
    .delete()
    .eq("vendor_id", vendorId)
    .eq("kind", "opening");
  if (delError) throw delError;

  if (asOf && openingBalance !== 0) {
    const { error: insError } = await supabase.from("vendor_ap_movements").insert({
      vendor_id: vendorId,
      movement_date: asOf,
      kind: "opening",
      amount: openingBalance,
      reference: "Opening balance",
      notes: "Brought-forward AP for ledger matching",
      source_type: "opening",
      source_id: vendorId,
    });
    if (insError) throw insError;
  }

  await recomputeVendorOutstanding(vendorId);
}

/** Remove a document-posted movement (e.g. cancelled PO bill) and refresh outstanding. */
export async function removePartyMovementBySource(args: {
  party: PartyKind;
  partyId: string;
  sourceType: string;
  sourceId: string;
}) {
  if (args.party === "client") {
    const { error } = await supabase
      .from("client_ar_movements")
      .delete()
      .eq("source_type", args.sourceType)
      .eq("source_id", args.sourceId);
    if (error) throw error;
    await recomputeClientOutstanding(args.partyId);
    return;
  }
  const { error } = await supabase
    .from("vendor_ap_movements")
    .delete()
    .eq("source_type", args.sourceType)
    .eq("source_id", args.sourceId);
  if (error) throw error;
  await recomputeVendorOutstanding(args.partyId);
}

/** Map signed amount to debit/credit for display. Positive → debit (increases balance). */
export function splitDebitCredit(amount: number): { debit: number; credit: number } {
  if (amount > 0) return { debit: amount, credit: 0 };
  if (amount < 0) return { debit: 0, credit: Math.abs(amount) };
  return { debit: 0, credit: 0 };
}

export function kindLabel(kind: string): string {
  const map: Record<string, string> = {
    opening: "Opening balance",
    invoice: "Invoice",
    receipt: "Payment received",
    credit_note: "Credit note",
    bill: "Purchase bill",
    payment: "Payment made",
    debit_note: "Debit note",
    adjustment: "Adjustment",
  };
  return map[kind] ?? kind;
}

/** Navigate from a ledger row into its originating document when possible. */
export type LedgerSourceLink =
  | { kind: "invoice"; id: string }
  | { kind: "credit_note"; id: string }
  | { kind: "client_payment"; clientId: string }
  | null;

export function resolveLedgerSourceLink(args: {
  sourceType?: string | null;
  sourceId?: string | null;
  partyId?: string | null;
}): LedgerSourceLink {
  const { sourceType, sourceId, partyId } = args;
  if (!sourceType) return null;
  if (sourceType === "invoice" && sourceId) return { kind: "invoice", id: sourceId };
  if (sourceType === "credit_note" && sourceId) return { kind: "credit_note", id: sourceId };
  if (sourceType === "client_payment" && partyId) {
    return { kind: "client_payment", clientId: partyId };
  }
  return null;
}

export { recomputeClientOutstanding, recomputeVendorOutstanding };
